import { Component, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { ImpersonationService } from "src/app/core/services/impersonation.service";
import { TenantContextService } from "src/app/core/services/tenant-context.service";

/**
 * I-5 (2026-05-06) — Sticky impersonation banner.
 *
 * Renders only while ImpersonationService.isActive(). Sits at the
 * top of the admin layout so the operator can never lose track of
 * acting-as-tenant-admin. The "Exit" button calls
 * ImpersonationService.exit() which restores the original
 * super_admin token + reloads tenant memberships.
 */
@Component({
  selector: "app-impersonation-banner",
  standalone: true,
  imports: [CommonModule, TranslateModule, MatIconModule, MatButtonModule],
  template: `
    <div *ngIf="impersonation.isActive()" class="imp-banner">
      <mat-icon class="imp-icon">switch_account</mat-icon>
      <div class="imp-text">
        <strong>{{ "impersonation.banner-title" | translate }}</strong>
        <span class="imp-target">
          {{ tenantName() || ("impersonation.unknown-tenant" | translate) }}
          <small *ngIf="impersonation.orgId()">#{{ impersonation.orgId() }}</small>
        </span>
      </div>
      <button mat-flat-button class="imp-exit" (click)="onExit()" [disabled]="exiting">
        <mat-icon>logout</mat-icon>
        {{ "impersonation.exit" | translate }}
      </button>
    </div>
  `,
  styles: [
    `
      .imp-banner {
        position: sticky;
        top: 0;
        z-index: 1100;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem 1rem;
        background: linear-gradient(90deg, #ba3939 0%, #d65151 100%);
        color: #fff;
        font-size: 0.875rem;
        box-shadow: 0 2px 8px rgba(186, 57, 57, 0.35);
      }
      .imp-icon {
        font-size: 1.25rem;
        width: 1.25rem;
        height: 1.25rem;
        line-height: 1.25rem;
      }
      .imp-text {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1;
        min-width: 0;
      }
      .imp-target {
        opacity: 0.92;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .imp-target small {
        opacity: 0.75;
        margin-inline-start: 0.25rem;
      }
      .imp-exit {
        background: rgba(255, 255, 255, 0.18) !important;
        color: #fff !important;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
      }
      .imp-exit:hover {
        background: rgba(255, 255, 255, 0.28) !important;
      }
    `,
  ],
})
export class ImpersonationBannerComponent {
  readonly impersonation = inject(ImpersonationService);
  private readonly _tenantContext = inject(TenantContextService);

  exiting = false;

  tenantName(): string | null {
    return this._tenantContext.active()?.nameAr ?? this._tenantContext.active()?.nameEn ?? null;
  }

  async onExit() {
    this.exiting = true;
    try {
      await this.impersonation.exit();
    } finally {
      this.exiting = false;
    }
  }
}
